import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PencilSimple, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatWorkDate, COMPANY_TZ_LABEL } from "@/lib/time";

/** Corrects ONE attendance record, addressed by its own record id.
 *  Sends only the record id — an employee's other shifts are never touched.
 */
export const EditShiftDialog = ({ entry, open, onOpenChange, onSaved }) => {
    const [sites, setSites] = useState([]);
    const [subs, setSubs] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !entry) return;
        setForm({
            site_id: entry.site_id,
            sublocation_id: entry.sublocation_id || "",
            clock_in_local: entry.clock_in_local || "",
            clock_out_local: entry.clock_out_local || "",
            notes: entry.notes || "",
            reason: "",
        });
        api.get("/sites").then(({ data }) => setSites(Array.isArray(data) ? data : [])).catch(() => setSites([]));
        api.get(`/sites/${entry.site_id}/sublocations`)
            .then(({ data }) => setSubs(Array.isArray(data) ? data : []))
            .catch(() => setSubs([]));
    }, [open, entry]);

    const changeSite = async (siteId) => {
        setForm((f) => ({ ...f, site_id: siteId, sublocation_id: "" }));
        try {
            const { data } = await api.get(`/sites/${siteId}/sublocations`);
            setSubs(Array.isArray(data) ? data : []);
        } catch {
            setSubs([]);
        }
    };

    const save = async () => {
        if (saving || !entry || !form) return;
        if ((form?.reason || "").trim().length < 3) {
            toast.error("Please enter a correction reason");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/time/entries/${entry.id}`, {
                site_id: form.site_id,
                sublocation_id: form.sublocation_id || null,
                clock_in_local: form.clock_in_local,
                clock_out_local: form.clock_out_local || null,
                notes: form.notes,
                reason: form.reason.trim(),
            });
            toast.success("Record corrected — logged in the audit trail");
            onOpenChange(false);
            onSaved?.(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-shift-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Correct time record
                    </DialogTitle>
                    <DialogDescription>
                        {entry?.technician_name} · {formatWorkDate(entry?.work_date)} — every change is logged
                        with your name in the audit trail.
                    </DialogDescription>
                </DialogHeader>

                {form && entry && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex gap-2"
                            data-testid="edit-shift-scope-note">
                            <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                            <span>
                                Only this record (<span className="font-mono">{entry.id.slice(0, 8)}</span>) changes.
                                Other shifts for this employee are untouched.
                            </span>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Site</Label>
                            <Select value={form.site_id} onValueChange={changeSite}>
                                <SelectTrigger className="h-11" data-testid="edit-shift-site-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {subs.length > 0 && (
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Data Hall / Sub-location
                                </Label>
                                <Select
                                    value={form.sublocation_id || "none"}
                                    onValueChange={(v) => setForm((f) => ({ ...f, sublocation_id: v === "none" ? "" : v }))}
                                >
                                    <SelectTrigger className="h-11" data-testid="edit-shift-sublocation-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— none —</SelectItem>
                                        {subs.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.name}{s.job_code ? ` · ${s.job_code}` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Clock In ({COMPANY_TZ_LABEL})
                                </Label>
                                <Input type="datetime-local" value={form.clock_in_local}
                                    onChange={(e) => setForm((f) => ({ ...f, clock_in_local: e.target.value }))}
                                    className="h-11" data-testid="edit-shift-clock-in-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Clock Out ({COMPANY_TZ_LABEL})
                                </Label>
                                <Input type="datetime-local" value={form.clock_out_local}
                                    onChange={(e) => setForm((f) => ({ ...f, clock_out_local: e.target.value }))}
                                    className="h-11" data-testid="edit-shift-clock-out-input" />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Correction reason <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Input value={form.reason}
                                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                                placeholder="e.g. Employee forgot to clock out"
                                className="h-11" data-testid="edit-shift-reason-input" />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Admin note (optional)
                            </Label>
                            <Textarea value={form.notes}
                                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                className="min-h-[64px]" data-testid="edit-shift-notes-input" />
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                        data-testid="edit-shift-cancel">
                        Cancel
                    </Button>
                    <Button onClick={save} disabled={saving}
                        className="bg-[#0066D9] hover:bg-[#0052ad] text-white font-semibold"
                        data-testid="edit-shift-save">
                        <PencilSimple size={16} className="mr-1.5" />
                        {saving ? "Saving…" : "Save correction"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
